import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { ChevronDown, User, Baby, Dog, Plus, Check } from 'lucide-react';

/**
 * ProfileSelector - Switch between family profiles
 */

const ProfileSelector = ({ profiles, selectedProfile, onSelectProfile, onAddProfile }) => {
  const getProfileIcon = (type) => {
    switch (type) {
      case 'child':
        return Baby;
      case 'pet':
        return Dog;
      default:
        return User;
    }
  };

  const getProfileTypeLabel = (profile) => {
    if (profile.relationship) {
      return profile.relationship;
    }
    return profile.profile_type === 'pet' ? 'Pet' : 
           profile.profile_type === 'child' ? 'Child' : 'Adult';
  };

  if (!selectedProfile) return null;

  const Icon = getProfileIcon(selectedProfile.profile_type);

  return (
    <div data-testid="profile-selector">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between h-14 px-4 bg-white"
            data-testid="profile-dropdown-trigger"
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                style={{ backgroundColor: selectedProfile.avatar_color || '#EF4444' }}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-800">{selectedProfile.name}</div>
                <div className="text-xs text-slate-500">{getProfileTypeLabel(selectedProfile)}</div>
              </div>
            </div>
            <ChevronDown className="h-5 w-5 text-slate-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
          {profiles.map((profile) => {
            const ProfileIcon = getProfileIcon(profile.profile_type);
            const isSelected = profile.id === selectedProfile.id;
            
            return (
              <DropdownMenuItem
                key={profile.id}
                onClick={() => onSelectProfile(profile)}
                className="py-3 cursor-pointer"
                data-testid={`profile-option-${profile.id}`}
              >
                <div className="flex items-center gap-3 flex-1">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                    style={{ backgroundColor: profile.avatar_color || '#EF4444' }}
                  >
                    <ProfileIcon className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-800">{profile.name}</div>
                    <div className="text-xs text-slate-500">{getProfileTypeLabel(profile)}</div>
                  </div>
                  {isSelected && <Check className="h-4 w-4 text-emerald-500" />}
                </div>
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onAddProfile}
            className="py-3 cursor-pointer"
            data-testid="add-new-profile-option"
          >
            <div className="flex items-center gap-3 text-red-600">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <Plus className="h-4 w-4" />
              </div>
              <span className="font-medium">Add New Profile</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ProfileSelector;
